import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Select from 'react-select';
import cx from 'classnames';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { ReactComponent as UploadFileIcon } from '../../../assets/icons/upload-icon-file.svg';
import MyGroupIcon from '../../../assets/icons/folder-open.svg';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import styles from './ImportUsersModal.module.scss';
import Modal from '../../modules/Modal';
import Search from '../../modules/Search';
import RoleDropdown from '../CreateSuperAdminModal/RoleDropDown';
import RolePlate from '../../modules/RolePlate';
import { getUsersByEmailArray, updateUserOrganization } from '../../../api';
import { USER } from '../../../enums';
import { DateFormatter } from '../../../helpers';
import Avatar from '../../modules/Avatar';
import { enqueueSnackbar } from 'notistack';
import { Tooltip } from '@material-ui/core';

type TImportUsersModal = {
  open: boolean;
  switchModal: () => void;
  fetchData: () => Promise<any>;
};

const levenshtein = (a: string, b: string) => {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b[i - 1] === a[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1, // deletion
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

const similarity = (s1: string, s2: string) => {
  const distance = levenshtein(s1, s2);
  const maxLen = Math.max(s1.length, s2.length);
  return maxLen === 0 ? 1 : 1 - distance / maxLen;
};

const ImportUsersModal: React.FC<TImportUsersModal> = ({ open, switchModal, fetchData }) => {
  const canViewEmails = useCanViewEmails();
  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [users, setUsers] = useState<any>([]);
  const [existingUsers, setExistingUsers] = useState<USER[]>([]);
  const [search, setSearch] = useState('');
  const { myOrganization } = useAppSelector((state) => state.user);

  const onClose = () => {
    switchModal();
    setSearch('');
    setUsers([]);
    setExistingUsers([]);
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      const usersToCreate = users.map((u: any) => {
        const obj = {
          ...u,
        };
        obj.role_id = normalizeRole(u.role).includes('admin') ? 'organization admin' : normalizeRole(u.role);
        if (obj.group.length) {
          obj.groups = obj.group.split(',');
        }
        return obj;
      });
      const res = await updateUserOrganization({ newUsers: usersToCreate });
      await fetchData();
      onClose();
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: any) => {
    // Helper: Calculate Levenshtein distance between two strings.
    const getLevenshteinDistance = (a: string, b: string): number => {
      const matrix = Array.from({ length: a.length + 1 }, (_, i) => Array(b.length + 1).fill(0));
      for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
      for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          matrix[i][j] =
            a[i - 1] === b[j - 1] ? matrix[i - 1][j - 1] : 1 + Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1]);
        }
      }
      return matrix[a.length][b.length];
    };

    // Helper: Correct the role based on similarity.
    const validRoles = ['VIEWER', 'ANALYST', 'ORGANIZATION ADMIN'];
    const getCorrectRole = (inputRole: string): string => {
      const roleUpper = inputRole.toUpperCase();
      if (validRoles.includes(roleUpper)) return roleUpper;

      let bestMatch = '';
      let minDistance = Infinity;
      for (const role of validRoles) {
        const distance = getLevenshteinDistance(roleUpper, role);
        if (distance < minDistance) {
          minDistance = distance;
          bestMatch = role;
        }
      }
      // Accept correction if the edit distance is small enough.
      return minDistance <= Math.floor(bestMatch.length / 2) ? bestMatch : inputRole;
    };

    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (evt: any) => {
      const binaryStr = evt.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const firstSheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheet];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      let success = true;

      // Process and correct each record.
      const processedData = jsonData
        .map((record: any) => {
          const lowered = Object.keys(record).reduce((acc: any, key) => {
            acc[key.toLowerCase()] = record[key];
            return acc;
          }, {});

          if (!lowered.email || !lowered.role) {
            success = false;
            return null;
          }

          const firstName = lowered.firstname || '';
          const lastName = lowered.lastname || '';
          const username = `${firstName} ${lastName}`.trim();

          return {
            email: lowered.email,
            username,
            role: getCorrectRole(lowered.role),
            group: lowered.group || '',
          };
        })
        .filter(Boolean)
        .filter((u) => u?.email !== 'example@user.com'); // Remove any null entries.

      if (!success) {
        enqueueSnackbar(`Invalid file`, {
          preventDuplicate: true,
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        return;
      } else {
        // @ts-ignore
        getUsersThatAlreadyExist(processedData);
        setUsers(processedData);
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  const getUsersThatAlreadyExist = async (usersArg: USER[]) => {
    setLoading(true);
    try {
      const res = await getUsersByEmailArray(
        usersArg.map((u) => u.email),
        myOrganization.id,
      );
      if (res?.data?.length) {
        setExistingUsers(res.data);
        const filteredUsers = usersArg.filter((u: USER) => {
          if (res.data.find((user: USER) => user.email.toLocaleLowerCase() == u.email.toLocaleLowerCase())) {
            return false;
          } else {
            return true;
          }
        });

        if (filteredUsers.length === 0) {
          enqueueSnackbar(`All users do exist`, {
            preventDuplicate: true,
            variant: 'warning',
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
          });
        }
        setUsers(filteredUsers);
      }
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  });

  const handleChange = (index: number, key: string, value: any) => {
    const updatedUsers = [...users];
    updatedUsers[index] = { ...updatedUsers[index], [key]: value };
    setUsers(updatedUsers);
  };

  // Remove user by index
  const removeUser = (index: number) => {
    const updatedUsers = [...users];
    updatedUsers.splice(index, 1);
    setUsers(updatedUsers);
  };

  // Map users with their original index to preserve unique keys even after filtering
  const filteredUsers: any = users
    .map((user: any, idx: number) => ({ user, idx }))
    .filter(
      ({ user }: any) =>
        search === '' || Object.values(user).some((val: any) => val.toString().toLowerCase().includes(search.toLowerCase())),
    );

  const normalizeRole = (role: string) => {
    const lowerRole = role.toLowerCase();
    // Replace with your similarity check logic (e.g., using a library or custom function)
    if (lowerRole === 'organisation admin' || similarity(lowerRole, 'organization admin') >= 0.9) {
      return 'organization admin';
    }
    return lowerRole;
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          Import users from CSV/XLSX file{' '}
          <div className={styles.modalTitleRightSide}>
            <Tooltip title={'Download template'}>
              <a style={{ color: 'white' }} href="/template.xlsx" download>
                <FileDownloadIcon />
              </a>
            </Tooltip>
            <CloseIcon className={styles.closeIcon} onClick={onClose} />
          </div>
        </div>

        {users.length === 0 && existingUsers.length === 0 && (
          <>
            <div className={styles.disclaimer}>
              Accepted fields: FirstName, LastName, Email, Role, Group (optional) <br />
            </div>
            <div {...getRootProps()} className={styles.dragDrop}>
              <input {...getInputProps()} />
              {isDragActive ? (
                <p>Drop the file here...</p>
              ) : (
                <div>
                  <div>
                    {' '}
                    <UploadFileIcon className={styles.dropIcon} />{' '}
                  </div>
                  Drag & drop or click to select <br />{' '}
                  <div style={{ fontSize: '12px', color: '#797979', marginTop: '4px' }}>CSV/XLSX supported </div>
                </div>
              )}
            </div>
          </>
        )}

        {existingUsers.length > 0 && (
          <>
            <div className={styles.listTitle}>Users that already exist</div>
            <div>
              <div className={styles.list}>
                {existingUsers.map((u) => (
                  <div className={styles.userContainer} key={u.id}>
                    <Avatar avatar={u?.avatar_url} name={u?.username || (canViewEmails ? u.email : 'User')} />
                    <input className={styles.value} value={u?.username} style={{ width: '100%' }} disabled />
                    {canViewEmails && <input className={styles.value} value={u.email} style={{ width: '100%' }} disabled />}
                    <input className={styles.value} value={DateFormatter(u.created_at, true)} style={{ width: '100%' }} disabled />

                    <div className={cx(styles.value, styles.roleContainer)}>
                      <RolePlate firstUpper className={styles.role} userRole={u?.role?.title} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {users.length > 0 && (
          <>
            <Search className={styles.search} onChange={(e) => setSearch(e.target.value)} placeHolder="Search users..." />
            <div className={styles.listTitle}>New Users</div>
            <form onSubmit={onSubmit}>
              <div className={styles.list}>
                {filteredUsers.map(({ user, idx }: any) => {
                  const orderedKeys = [
                    'username',
                    'email',
                    'role',
                    ...Object.keys(user).filter((key) => !['username', 'email', 'role'].includes(key)),
                  ];
                  return (
                    <div className={styles.userContainer} key={idx}>
                      <Avatar avatar={''} name={user.username || (canViewEmails ? user.email : 'User')} />
                      {orderedKeys.map((key) => {
                        if (key === 'role') {
                          return (
                            <div className={cx(styles.value, styles.roleContainer)} key={key}>
                              <RolePlate
                                firstUpper
                                className={styles.role}
                                userRole={normalizeRole(user[key]).includes('admin') ? 'oa' : normalizeRole(user[key])}
                              />
                            </div>
                          );
                        }

                        if (key === 'group') {
                          if (user[key].length === 0) {
                            return (
                              <div className={styles.groupsValue}>
                                <img className={styles.groupImage} src={MyGroupIcon} alt="groups management" />
                                <div>0</div>
                              </div>
                            );
                          } else {
                            return (
                              <Tooltip
                                arrow
                                title={user[key].split(',').map((g: string) => (
                                  <div className={styles.tooltip}>
                                    <img className={styles.groupImage} src={MyGroupIcon} alt="groups management" />
                                    <div>{g}</div>
                                  </div>
                                ))}
                              >
                                <div className={styles.groupsValue}>
                                  <img className={styles.groupImage} src={MyGroupIcon} alt="groups management" />
                                  <div>{user[key].split(',').length}</div>
                                </div>
                              </Tooltip>
                            );
                          }
                        }
                        return (
                          <input
                            className={styles.value}
                            key={key}
                            type={key === 'email' ? 'email' : 'text'}
                            value={key === 'email' ? user[key].toLocaleLowerCase() : user[key]}
                            onChange={(e) => handleChange(idx, key, key === 'email' ? e.target.value.toLocaleLowerCase() : e.target.value)}
                            style={{ width: key === 'role' ? '105px' : '100%' }}
                            required
                          />
                        );
                      })}

                      <OutlinedButton className={styles.removeUserBtn} onClick={() => removeUser(idx)} kind="default">
                        Remove
                      </OutlinedButton>
                    </div>
                  );
                })}
              </div>
              {users?.length > 0 && (
                <ContainedButton disabled={loading} className={styles.addUsersBtn} type="submit">
                  Add Users
                </ContainedButton>
              )}
            </form>
          </>
        )}

        <div className={styles.actionBtns}>
          <OutlinedButton onClick={onClose} kind="default">
            Cancel
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default ImportUsersModal;
